import { ILayoutBaseTestGen } from '../layoutBaseTest';
import {
  PdfGen,
  PdfFont,
  PdfRect,
  PdfBrush,
  ETextAlign,
  PdfUnit,
  PdfSize,
  PageGen,
  PdfPen,
  PdfColor,
} from '../../pdf/pdf';
import '../../pdf/draw/string';
import '../../pdf/draw/primitives';
import '../../pdf/draw/shapes';
import { moduleRegistry } from '../../modules';
import { textModule } from '../../modules/text';
import { dotModule } from '../../modules/dot';
import { latexModule } from '../../modules/latex';
import { clockModule } from '../../modules/clock';
import { OptsBaseTest } from '../optsBaseTest';

const MM = PdfUnit.FromMillimeters;
const MARGIN = MM(12);

export class TestDot implements ILayoutBaseTestGen {
  async generate(pdf: PdfGen, opts: OptsBaseTest): Promise<void> {
    await pdf.addStandardFonts();

    moduleRegistry.register(textModule);
    moduleRegistry.register(dotModule);
    moduleRegistry.register(latexModule);
    moduleRegistry.register(clockModule);

    const font = new PdfFont(PdfFont.FONT_NAME, 14);
    const smallFont = new PdfFont(PdfFont.FONT_NAME, 10);

    // Page 1: Basic Graphs
    const page1 = pdf.addPage();
    const titleRect = new PdfRect(MARGIN, MM(5), PdfSize.A4.width - 2 * MARGIN, MM(8));
    page1.drawString(
      'DOT Module Tests - Basic Graphs',
      font,
      PdfBrush.Black,
      null,
      titleRect,
      ETextAlign.CenterLeft
    );

    const dot = moduleRegistry.get('dot');
    if (!dot) return;

    // Test 1: Basic linear flow
    const basicGraph = `digraph G { start -> a0; a0 -> a1; a1 -> a2; a2 -> end; }`;
    const rect1 = new PdfRect(MARGIN, MM(30), MM(90), MM(80));
    page1.drawRectangle(new PdfPen(PdfColor.fromRgb(200, 200, 200), 0.5), null, rect1);
    page1.drawString(
      '1. Basic Linear Flow:',
      smallFont,
      PdfBrush.Black,
      null,
      new PdfRect(MARGIN, MM(25), MM(150), MM(5)),
      ETextAlign.CenterLeft
    );
    const content1 = dot.parse(basicGraph, {});
    dot.draw(content1, rect1, ETextAlign.CenterLeft, { font, page: page1 });

    // Test 2: Node shapes
    const nodeShapes = `digraph G { 
      diamond [shape=Mdiamond]; 
      square [shape=Msquare]; 
      box [shape=box]; 
      ellipse [shape=ellipse];
      circle [shape=circle];
      diamond -> box;
      box -> ellipse;
      ellipse -> circle;
      circle -> square;
    }`;
    const rect2 = new PdfRect(MM(110), MM(30), MM(87), MM(80));
    page1.drawRectangle(new PdfPen(PdfColor.fromRgb(200, 200, 200), 0.5), null, rect2);
    page1.drawString(
      '2. Node Shapes:',
      smallFont,
      PdfBrush.Black,
      null,
      new PdfRect(MM(110), MM(25), MM(87), MM(5)),
      ETextAlign.CenterLeft
    );
    const content2 = dot.parse(nodeShapes, {});
    dot.draw(content2, rect2, ETextAlign.CenterLeft, { font, page: page1 });

    // Test 3: Embedded modules in labels
    const embeddedLabels = `digraph G {
      node1 [label=<latex>\\alpha</latex>];
      node2 [label="Plain text"];
      node3 [label=<latex>\\beta + \\gamma</latex>];
      node4 [label="Mixed: <latex>x^2</latex>"];
      node1 -> node2;
      node2 -> node3;
      node3 -> node4;
    }`;
    const rect3 = new PdfRect(MARGIN, MM(120), MM(180), MM(80));
    page1.drawRectangle(new PdfPen(PdfColor.fromRgb(200, 200, 200), 0.5), null, rect3);
    page1.drawString(
      '3. Embedded Module Labels:',
      smallFont,
      PdfBrush.Black,
      null,
      new PdfRect(MARGIN, MM(115), MM(180), MM(5)),
      ETextAlign.CenterLeft
    );
    const content3 = dot.parse(embeddedLabels, {});
    dot.draw(content3, rect3, ETextAlign.CenterLeft, { font, page: page1 });

    // Page 2: Graph Structures
    const page2 = pdf.addPage();
    page2.drawString(
      'DOT Module Tests - Graph Structures',
      font,
      PdfBrush.Black,
      null,
      titleRect,
      ETextAlign.CenterLeft
    );

    // Test 4: Tree structure
    const treeGraph = `digraph G {
      node [shape=box];
      root -> {a b c};
      a -> {a1 a2};
      b -> {b1 b2};
      c -> {c1 c2};
    }`;
    const rect4 = new PdfRect(MARGIN, MM(30), MM(180), MM(100));
    page2.drawRectangle(new PdfPen(PdfColor.fromRgb(200, 200, 200), 0.5), null, rect4);
    page2.drawString(
      '4. Tree Structure:',
      smallFont,
      PdfBrush.Black,
      null,
      new PdfRect(MARGIN, MM(25), MM(150), MM(5)),
      ETextAlign.CenterLeft
    );
    const content4 = dot.parse(treeGraph, {});
    dot.draw(content4, rect4, ETextAlign.CenterLeft, { font, page: page2 });

    // Test 5: Diamond structure
    const diamondGraph = `digraph G {
      start -> {a b};
      a -> end;
      b -> end;
    }`;
    const rect5 = new PdfRect(MM(110), MM(30), MM(87), MM(100));
    page2.drawRectangle(new PdfPen(PdfColor.fromRgb(200, 200, 200), 0.5), null, rect5);
    page2.drawString(
      '5. Diamond Structure:',
      smallFont,
      PdfBrush.Black,
      null,
      new PdfRect(MM(110), MM(25), MM(87), MM(5)),
      ETextAlign.CenterLeft
    );
    const content5 = dot.parse(diamondGraph, {});
    dot.draw(content5, rect5, ETextAlign.CenterLeft, { font, page: page2 });

    // Page 3: Edge Weights and Rank Constraints
    const page3 = pdf.addPage();
    page3.drawString(
      'DOT Module Tests - Weights & Constraints',
      font,
      PdfBrush.Black,
      null,
      titleRect,
      ETextAlign.CenterLeft
    );

    // Test 6: Edge weights (should influence crossing reduction)
    const weightedGraph = `digraph G {
      node [shape=box];
      a -> b [weight=5];
      a -> c [weight=1];
      b -> d [weight=2];
      c -> d [weight=5];
      d -> e;
    }`;
    const rect6 = new PdfRect(MARGIN, MM(30), MM(90), MM(100));
    page3.drawRectangle(new PdfPen(PdfColor.fromRgb(200, 200, 200), 0.5), null, rect6);
    page3.drawString(
      '6. Edge Weights:',
      smallFont,
      PdfBrush.Black,
      null,
      new PdfRect(MARGIN, MM(25), MM(150), MM(5)),
      ETextAlign.CenterLeft
    );
    page3.drawString(
      '(Weights should minimize crossings)',
      smallFont,
      PdfBrush.Black,
      null,
      new PdfRect(MARGIN, MM(40), MM(180), MM(5)),
      ETextAlign.CenterLeft
    );
    const content6 = dot.parse(weightedGraph, {});
    dot.draw(content6, rect6, ETextAlign.CenterLeft, { font, page: page3 });

    // Test 7: Rank constraints
    const rankConstraints = `digraph G {
      node [shape=box];
      {rank=same; a; b; c}
      {rank=same; d; e}
      a -> b;
      b -> c;
      c -> d;
      d -> e;
      b -> e [constraint=false];
    }`;
    const rect7 = new PdfRect(MM(110), MM(30), MM(87), MM(100));
    page3.drawRectangle(new PdfPen(PdfColor.fromRgb(200, 200, 200), 0.5), null, rect7);
    page3.drawString(
      '7. Rank Constraints:',
      smallFont,
      PdfBrush.Black,
      null,
      new PdfRect(MM(110), MM(25), MM(150), MM(5)),
      ETextAlign.CenterLeft
    );
    page3.drawString(
      '(a,b,c should be same rank)',
      smallFont,
      PdfBrush.Black,
      null,
      new PdfRect(MM(110), MM(40), MM(180), MM(5)),
      ETextAlign.CenterLeft
    );
    const content7 = dot.parse(rankConstraints, {});
    dot.draw(content7, rect7, ETextAlign.CenterLeft, { font, page: page3 });

    // Page 4: Cyclic Graphs and Complex Cases
    const page4 = pdf.addPage();
    page4.drawString(
      'DOT Module Tests - Cycles & Complex Cases',
      font,
      PdfBrush.Black,
      null,
      titleRect,
      ETextAlign.CenterLeft
    );

    // Test 8: Cyclic graph (should be handled gracefully)
    const cyclicGraph = `digraph G {
      node [shape=box];
      a -> b;
      b -> c;
      c -> d;
      d -> a;  // Creates cycle
      b -> e;
      e -> f;
    }`;
    const rect8 = new PdfRect(MARGIN, MM(30), MM(90), MM(100));
    page4.drawRectangle(new PdfPen(PdfColor.fromRgb(200, 200, 200), 0.5), null, rect8);
    page4.drawString(
      '8. Cyclic Graph:',
      smallFont,
      PdfBrush.Black,
      null,
      new PdfRect(MARGIN, MM(25), MM(150), MM(5)),
      ETextAlign.CenterLeft
    );
    page4.drawString(
      '(Cycle a-b-c-d should be handled)',
      smallFont,
      PdfBrush.Black,
      null,
      new PdfRect(MARGIN, MM(40), MM(180), MM(5)),
      ETextAlign.CenterLeft
    );
    const content8 = dot.parse(cyclicGraph, {});
    dot.draw(content8, rect8, ETextAlign.CenterLeft, { font, page: page4 });

    // Test 9: Cluster subgraphs
    const clusterGraph = `digraph G {
      subgraph cluster_0 {
        style=filled;
        color=lightgrey;
        node [style=filled,color=white];
        a0 -> a1 -> a2 -> a3;
        label = "Cluster A";
      }
      subgraph cluster_1 {
        node [style=filled];
        b0 -> b1 -> b2 -> b3;
        label = "Cluster B";
        color=blue;
      }
      start -> a0;
      start -> b0;
      a1 -> b3;
      b2 -> a3;
      a3 -> end;
      b3 -> end;
    }`;
    const rect9 = new PdfRect(MM(110), MM(30), MM(87), MM(100));
    page4.drawRectangle(new PdfPen(PdfColor.fromRgb(200, 200, 200), 0.5), null, rect9);
    page4.drawString(
      '9. Cluster Subgraphs:',
      smallFont,
      PdfBrush.Black,
      null,
      new PdfRect(MM(110), MM(25), MM(150), MM(5)),
      ETextAlign.CenterLeft
    );
    const content9 = dot.parse(clusterGraph, {});
    dot.draw(content9, rect9, ETextAlign.CenterLeft, { font, page: page4 });

    // Page 5: Edge Routing Styles
    const page5 = pdf.addPage();
    page5.drawString(
      'DOT Module Tests - Edge Routing',
      font,
      PdfBrush.Black,
      null,
      titleRect,
      ETextAlign.CenterLeft
    );

    // Test 10: Orthogonal edges (concentrate=true)
    const orthogonalGraph = `digraph G {
      concentrate=true;
      node [shape=box];
      a -> b;
      b -> c;
      c -> d;
      a -> c;
      b -> d;
    }`;
    const rect10 = new PdfRect(MARGIN, MM(30), MM(90), MM(100));
    page5.drawRectangle(new PdfPen(PdfColor.fromRgb(200, 200, 200), 0.5), null, rect10);
    page5.drawString(
      '10. Orthogonal Edges (concentrate=true):',
      smallFont,
      PdfBrush.Black,
      null,
      new PdfRect(MARGIN, MM(25), MM(200), MM(5)),
      ETextAlign.CenterLeft
    );
    const content10 = dot.parse(orthogonalGraph, {});
    dot.draw(content10, rect10, ETextAlign.CenterLeft, { font, page: page5 });

    // Test 11: Spline edges (concentrate=false)
    const splineGraph = `digraph G {
      concentrate=false;
      node [shape=box];
      a -> b;
      b -> c;
      c -> d;
      a -> c;
      b -> d;
    }`;
    const rect11 = new PdfRect(MM(110), MM(30), MM(87), MM(100));
    page5.drawRectangle(new PdfPen(PdfColor.fromRgb(200, 200, 200), 0.5), null, rect11);
    page5.drawString(
      '11. Spline Edges (concentrate=false):',
      smallFont,
      PdfBrush.Black,
      null,
      new PdfRect(MM(110), MM(25), MM(200), MM(5)),
      ETextAlign.CenterLeft
    );
    const content11 = dot.parse(splineGraph, {});
    dot.draw(content11, rect11, ETextAlign.CenterLeft, { font, page: page5 });

    // Test 12: Edge styles and arrows
    const edgeStyles = `digraph G {
      node [shape=box];
      a -> b [style=dashed];
      a -> c [style=dotted];
      b -> d [color=red];
      c -> d [color=blue, penwidth=3];
      d -> e [arrowhead=vee];
      e -> f [arrowhead=dot];
    }`;
    const rect12 = new PdfRect(MARGIN, MM(140), MM(180), MM(80));
    page5.drawRectangle(new PdfPen(PdfColor.fromRgb(200, 200, 200), 0.5), null, rect12);
    page5.drawString(
      '12. Edge Styles & Arrows:',
      smallFont,
      PdfBrush.Black,
      null,
      new PdfRect(MARGIN, MM(135), MM(200), MM(5)),
      ETextAlign.CenterLeft
    );
    const content12 = dot.parse(edgeStyles, {});
    dot.draw(content12, rect12, ETextAlign.CenterLeft, { font, page: page5 });

    // Page 6: Algorithm Verification Tests
    const page6 = pdf.addPage();
    page6.drawString(
      'DOT Module Tests - Algorithm Verification',
      font,
      PdfBrush.Black,
      null,
      titleRect,
      ETextAlign.CenterLeft
    );

    // Test 13: Graph from documentation example
    const docGraph = `digraph G {
      rankdir=LR;
      node [shape=box];
      Input -> Parser;
      Parser -> AST;
      AST -> Layout;
      Layout -> Renderer;
      Renderer -> Output;
      
      {rank=same; Parser; AST; Layout}
      {rank=same; Input; Output}
    }`;
    const rect13 = new PdfRect(MARGIN, MM(30), MM(180), MM(100));
    page6.drawRectangle(new PdfPen(PdfColor.fromRgb(200, 200, 200), 0.5), null, rect13);
    page6.drawString(
      '13. Documentation Example (LR rankdir):',
      smallFont,
      PdfBrush.Black,
      null,
      new PdfRect(MARGIN, MM(25), MM(300), MM(5)),
      ETextAlign.CenterLeft
    );
    const content13 = dot.parse(docGraph, {});
    dot.draw(content13, rect13, ETextAlign.CenterLeft, { font, page: page6 });

    // Test 14: Complex crossing minimization
    const crossingGraph = `digraph G {
      node [shape=circle];
      a -> d;
      a -> e;
      b -> d;
      b -> f;
      c -> e;
      c -> f;
      
      // Should minimize crossings between a-d/e and b-d/f edges
    }`;
    const rect14 = new PdfRect(MARGIN, MM(140), MM(180), MM(80));
    page6.drawRectangle(new PdfPen(PdfColor.fromRgb(200, 200, 200), 0.5), null, rect14);
    page6.drawString(
      '14. Crossing Minimization Test:',
      smallFont,
      PdfBrush.Black,
      null,
      new PdfRect(MARGIN, MM(135), MM(300), MM(5)),
      ETextAlign.CenterLeft
    );
    page6.drawString(
      '(Check edge crossing count)',
      smallFont,
      PdfBrush.Black,
      null,
      new PdfRect(MARGIN, MM(145), MM(300), MM(5)),
      ETextAlign.CenterLeft
    );
    const content14 = dot.parse(crossingGraph, {});
    dot.draw(content14, rect14, ETextAlign.CenterLeft, { font, page: page6 });

    // Page 7: Spline Tests
    const page7 = pdf.addPage();
    page7.drawString(
      'DOT Module Tests - Spline Rendering',
      font,
      PdfBrush.Black,
      null,
      titleRect,
      ETextAlign.CenterLeft
    );

    // Test 15: Complex multi-edge spline routing
    const splineComplex1 = `digraph G {
      concentrate=false;
      node [shape=box];
      a -> b;
      a -> c;
      a -> d;
      b -> e;
      c -> e;
      d -> e;
      a -> e;
    }`;
    const rect15 = new PdfRect(MARGIN, MM(30), MM(90), MM(100));
    page7.drawRectangle(new PdfPen(PdfColor.fromRgb(200, 200, 200), 0.5), null, rect15);
    page7.drawString(
      '15. Multi-Edge Splines:',
      smallFont,
      PdfBrush.Black,
      null,
      new PdfRect(MARGIN, MM(25), MM(200), MM(5)),
      ETextAlign.CenterLeft
    );
    const content15 = dot.parse(splineComplex1, {});
    dot.draw(content15, rect15, ETextAlign.CenterLeft, { font, page: page7 });

    // Test 16: Spline with back edges
    const splineBackEdges = `digraph G {
      concentrate=false;
      node [shape=box];
      a -> b -> c -> d;
      b -> d;
      c -> a;
    }`;
    const rect16 = new PdfRect(MM(110), MM(30), MM(87), MM(100));
    page7.drawRectangle(new PdfPen(PdfColor.fromRgb(200, 200, 200), 0.5), null, rect16);
    page7.drawString(
      '16. Spline Back Edges:',
      smallFont,
      PdfBrush.Black,
      null,
      new PdfRect(MM(110), MM(25), MM(200), MM(5)),
      ETextAlign.CenterLeft
    );
    const content16 = dot.parse(splineBackEdges, {});
    dot.draw(content16, rect16, ETextAlign.CenterLeft, { font, page: page7 });

    // Test 17: LR rankdir with splines
    const splineLR = `digraph G {
      rankdir=LR;
      concentrate=false;
      node [shape=box];
      a -> b -> c -> d -> e;
      a -> c;
      b -> d;
      c -> e;
    }`;
    const rect17 = new PdfRect(MARGIN, MM(140), MM(180), MM(80));
    page7.drawRectangle(new PdfPen(PdfColor.fromRgb(200, 200, 200), 0.5), null, rect17);
    page7.drawString(
      '17. LR Spline Routing:',
      smallFont,
      PdfBrush.Black,
      null,
      new PdfRect(MARGIN, MM(135), MM(200), MM(5)),
      ETextAlign.CenterLeft
    );
    const content17 = dot.parse(splineLR, {});
    dot.draw(content17, rect17, ETextAlign.CenterLeft, { font, page: page7 });

    // Page 8: More Spline Tests
    const page8 = pdf.addPage();
    page8.drawString(
      'DOT Module Tests - Spline Variations',
      font,
      PdfBrush.Black,
      null,
      titleRect,
      ETextAlign.CenterLeft
    );

    // Test 18: Dense edge connections with splines
    const splineDense = `digraph G {
      concentrate=false;
      node [shape=circle];
      a -> {b c d e};
      {b c d} -> f;
    }`;
    const rect18 = new PdfRect(MARGIN, MM(30), MM(90), MM(100));
    page8.drawRectangle(new PdfPen(PdfColor.fromRgb(200, 200, 200), 0.5), null, rect18);
    page8.drawString(
      '18. Dense Spline Edges:',
      smallFont,
      PdfBrush.Black,
      null,
      new PdfRect(MARGIN, MM(25), MM(200), MM(5)),
      ETextAlign.CenterLeft
    );
    const content18 = dot.parse(splineDense, {});
    dot.draw(content18, rect18, ETextAlign.CenterLeft, { font, page: page8 });

    // Test 19: Spline with rank constraints
    const splineRanks = `digraph G {
      concentrate=false;
      node [shape=box];
      {rank=same; a1 a2 a3}
      {rank=same; b1 b2 b3}
      a1 -> b1; a1 -> b2; a1 -> b3;
      a2 -> b1; a2 -> b2; a2 -> b3;
      a3 -> b1; a3 -> b2; a3 -> b3;
    }`;
    const rect19 = new PdfRect(MM(110), MM(30), MM(87), MM(100));
    page8.drawRectangle(new PdfPen(PdfColor.fromRgb(200, 200, 200), 0.5), null, rect19);
    page8.drawString(
      '19. Spline Grid Layout:',
      smallFont,
      PdfBrush.Black,
      null,
      new PdfRect(MM(110), MM(25), MM(200), MM(5)),
      ETextAlign.CenterLeft
    );
    const content19 = dot.parse(splineRanks, {});
    dot.draw(content19, rect19, ETextAlign.CenterLeft, { font, page: page8 });

    // Test 20: Spline with long edges
    const splineLong = `digraph G {
      concentrate=false;
      node [shape=box];
      start -> middle1 -> middle2 -> middle3 -> end;
      start -> middle2;
      middle1 -> end;
      start -> end;
    }`;
    const rect20 = new PdfRect(MARGIN, MM(140), MM(180), MM(80));
    page8.drawRectangle(new PdfPen(PdfColor.fromRgb(200, 200, 200), 0.5), null, rect20);
    page8.drawString(
      '20. Long Spline Edges:',
      smallFont,
      PdfBrush.Black,
      null,
      new PdfRect(MARGIN, MM(135), MM(200), MM(5)),
      ETextAlign.CenterLeft
    );
    const content20 = dot.parse(splineLong, {});
    dot.draw(content20, rect20, ETextAlign.CenterLeft, { font, page: page8 });

    // Page 9: Advanced Spline Tests
    const page9 = pdf.addPage();
    page9.drawString(
      'DOT Module Tests - Advanced Splines',
      font,
      PdfBrush.Black,
      null,
      titleRect,
      ETextAlign.CenterLeft
    );

    // Test 21: Spline with clusters
    const splineClusters = `digraph G {
      concentrate=false;
      subgraph cluster_0 {
        a -> b -> c;
        label = "Cluster A";
      }
      subgraph cluster_1 {
        d -> e -> f;
        label = "Cluster B";
      }
      c -> d;
      b -> e;
    }`;
    const rect21 = new PdfRect(MARGIN, MM(30), MM(90), MM(100));
    page9.drawRectangle(new PdfPen(PdfColor.fromRgb(200, 200, 200), 0.5), null, rect21);
    page9.drawString(
      '21. Spline with Clusters:',
      smallFont,
      PdfBrush.Black,
      null,
      new PdfRect(MARGIN, MM(25), MM(200), MM(5)),
      ETextAlign.CenterLeft
    );
    const content21 = dot.parse(splineClusters, {});
    dot.draw(content21, rect21, ETextAlign.CenterLeft, { font, page: page9 });

    // Test 22: BT rankdir with splines
    const splineBT = `digraph G {
      rankdir=BT;
      concentrate=false;
      node [shape=box];
      a -> b -> c -> d;
      a -> c;
      b -> d;
    }`;
    const rect22 = new PdfRect(MM(110), MM(30), MM(87), MM(100));
    page9.drawRectangle(new PdfPen(PdfColor.fromRgb(200, 200, 200), 0.5), null, rect22);
    page9.drawString(
      '22. BT Spline Routing:',
      smallFont,
      PdfBrush.Black,
      null,
      new PdfRect(MM(110), MM(25), MM(200), MM(5)),
      ETextAlign.CenterLeft
    );
    const content22 = dot.parse(splineBT, {});
    dot.draw(content22, rect22, ETextAlign.CenterLeft, { font, page: page9 });

    // Test 23: Spline with colored edges
    const splineColored = `digraph G {
      concentrate=false;
      node [shape=box];
      a -> b [color=red];
      a -> c [color=green];
      a -> d [color=blue];
      b -> e [color=purple];
      c -> e [color=orange];
      d -> e [color=brown];
    }`;
    const rect23 = new PdfRect(MARGIN, MM(140), MM(180), MM(80));
    page9.drawRectangle(new PdfPen(PdfColor.fromRgb(200, 200, 200), 0.5), null, rect23);
    page9.drawString(
      '23. Colored Spline Edges:',
      smallFont,
      PdfBrush.Black,
      null,
      new PdfRect(MARGIN, MM(135), MM(200), MM(5)),
      ETextAlign.CenterLeft
    );
    const content23 = dot.parse(splineColored, {});
    dot.draw(content23, rect23, ETextAlign.CenterLeft, { font, page: page9 });

    // Page 10: Spline Edge Styles
    const page10 = pdf.addPage();
    page10.drawString(
      'DOT Module Tests - Spline Edge Styles',
      font,
      PdfBrush.Black,
      null,
      titleRect,
      ETextAlign.CenterLeft
    );

    // Test 24: Dashed and dotted splines
    const splineStyles = `digraph G {
      concentrate=false;
      node [shape=box];
      a -> b [style=dashed];
      a -> c [style=dotted];
      a -> d [style=solid];
      b -> e;
      c -> e;
      d -> e;
    }`;
    const rect24 = new PdfRect(MARGIN, MM(30), MM(90), MM(100));
    page10.drawRectangle(new PdfPen(PdfColor.fromRgb(200, 200, 200), 0.5), null, rect24);
    page10.drawString(
      '24. Spline Edge Styles:',
      smallFont,
      PdfBrush.Black,
      null,
      new PdfRect(MARGIN, MM(25), MM(200), MM(5)),
      ETextAlign.CenterLeft
    );
    const content24 = dot.parse(splineStyles, {});
    dot.draw(content24, rect24, ETextAlign.CenterLeft, { font, page: page10 });

    // Test 25: Spline with arrowheads
    const splineArrows = `digraph G {
      concentrate=false;
      node [shape=box];
      a -> b [arrowhead=normal];
      b -> c [arrowhead=vee];
      c -> d [arrowhead=dot];
      a -> d [arrowhead=crow];
    }`;
    const rect25 = new PdfRect(MM(110), MM(30), MM(87), MM(100));
    page10.drawRectangle(new PdfPen(PdfColor.fromRgb(200, 200, 200), 0.5), null, rect25);
    page10.drawString(
      '25. Spline Arrowheads:',
      smallFont,
      PdfBrush.Black,
      null,
      new PdfRect(MM(110), MM(25), MM(200), MM(5)),
      ETextAlign.CenterLeft
    );
    const content25 = dot.parse(splineArrows, {});
    dot.draw(content25, rect25, ETextAlign.CenterLeft, { font, page: page10 });

    // Test 26: Wide fan-out with splines
    const splineFanout = `digraph G {
      concentrate=false;
      node [shape=circle];
      center -> {a b c d e f g h};
    }`;
    const rect26 = new PdfRect(MARGIN, MM(140), MM(180), MM(80));
    page10.drawRectangle(new PdfPen(PdfColor.fromRgb(200, 200, 200), 0.5), null, rect26);
    page10.drawString(
      '26. Spline Fan-Out:',
      smallFont,
      PdfBrush.Black,
      null,
      new PdfRect(MARGIN, MM(135), MM(200), MM(5)),
      ETextAlign.CenterLeft
    );
    const content26 = dot.parse(splineFanout, {});
    dot.draw(content26, rect26, ETextAlign.CenterLeft, { font, page: page10 });

    // Page 11: Color Tests - Node Colors
    const page11 = pdf.addPage();
    page11.drawString(
      'DOT Module Tests - Node Colors',
      font,
      PdfBrush.Black,
      null,
      titleRect,
      ETextAlign.CenterLeft
    );

    // Test 27: Node fill colors
    const nodeFillColors = `digraph G {
      a [fillcolor=red];
      b [fillcolor=green];
      c [fillcolor=blue];
      d [fillcolor=yellow];
      a -> b -> c -> d;
    }`;
    const rect27 = new PdfRect(MARGIN, MM(30), MM(90), MM(100));
    page11.drawRectangle(new PdfPen(PdfColor.fromRgb(200, 200, 200), 0.5), null, rect27);
    page11.drawString(
      '27. Node Fill Colors:',
      smallFont,
      PdfBrush.Black,
      null,
      new PdfRect(MARGIN, MM(25), MM(200), MM(5)),
      ETextAlign.CenterLeft
    );
    const content27 = dot.parse(nodeFillColors, {});
    dot.draw(content27, rect27, ETextAlign.CenterLeft, { font, page: page11 });

    // Test 28: Node border (stroke) colors
    const nodeBorderColors = `digraph G {
      a [color=red];
      b [color=green];
      c [color=blue];
      d [color=purple];
      a -> b -> c -> d;
    }`;
    const rect28 = new PdfRect(MM(110), MM(30), MM(87), MM(100));
    page11.drawRectangle(new PdfPen(PdfColor.fromRgb(200, 200, 200), 0.5), null, rect28);
    page11.drawString(
      '28. Node Border Colors:',
      smallFont,
      PdfBrush.Black,
      null,
      new PdfRect(MM(110), MM(25), MM(200), MM(5)),
      ETextAlign.CenterLeft
    );
    const content28 = dot.parse(nodeBorderColors, {});
    dot.draw(content28, rect28, ETextAlign.CenterLeft, { font, page: page11 });

    // Test 29: Combined fill and border colors
    const nodeBothColors = `digraph G {
      a [fillcolor=red, color=black];
      b [fillcolor=green, color=white];
      c [fillcolor=blue, color=yellow];
      d [fillcolor=yellow, color=red];
      a -> b -> c -> d;
    }`;
    const rect29 = new PdfRect(MARGIN, MM(140), MM(180), MM(80));
    page11.drawRectangle(new PdfPen(PdfColor.fromRgb(200, 200, 200), 0.5), null, rect29);
    page11.drawString(
      '29. Fill + Border Colors:',
      smallFont,
      PdfBrush.Black,
      null,
      new PdfRect(MARGIN, MM(135), MM(200), MM(5)),
      ETextAlign.CenterLeft
    );
    const content29 = dot.parse(nodeBothColors, {});
    dot.draw(content29, rect29, ETextAlign.CenterLeft, { font, page: page11 });

    // Page 12: Edge Colors and Thickness
    const page12 = pdf.addPage();
    page12.drawString(
      'DOT Module Tests - Edge Styles',
      font,
      PdfBrush.Black,
      null,
      titleRect,
      ETextAlign.CenterLeft
    );

    // Test 30: Edge colors
    const edgeColors = `digraph G {
      a -> b [color=red];
      a -> c [color=green];
      a -> d [color=blue];
      a -> e [color=purple];
    }`;
    const rect30 = new PdfRect(MARGIN, MM(30), MM(90), MM(100));
    page12.drawRectangle(new PdfPen(PdfColor.fromRgb(200, 200, 200), 0.5), null, rect30);
    page12.drawString(
      '30. Edge Colors:',
      smallFont,
      PdfBrush.Black,
      null,
      new PdfRect(MARGIN, MM(25), MM(200), MM(5)),
      ETextAlign.CenterLeft
    );
    const content30 = dot.parse(edgeColors, {});
    dot.draw(content30, rect30, ETextAlign.CenterLeft, { font, page: page12 });

    // Test 31: Edge thickness (penwidth)
    const edgeThickness = `digraph G {
      a -> b [penwidth=1];
      a -> c [penwidth=2];
      a -> d [penwidth=3];
      a -> e [penwidth=5];
    }`;
    const rect31 = new PdfRect(MM(110), MM(30), MM(87), MM(100));
    page12.drawRectangle(new PdfPen(PdfColor.fromRgb(200, 200, 200), 0.5), null, rect31);
    page12.drawString(
      '31. Edge Thickness:',
      smallFont,
      PdfBrush.Black,
      null,
      new PdfRect(MM(110), MM(25), MM(200), MM(5)),
      ETextAlign.CenterLeft
    );
    const content31 = dot.parse(edgeThickness, {});
    dot.draw(content31, rect31, ETextAlign.CenterLeft, { font, page: page12 });

    // Test 32: Combined edge color + thickness + style
    const edgeCombined = `digraph G {
      a -> b [color=red, penwidth=2, style=solid];
      a -> c [color=green, penwidth=3, style=dashed];
      a -> d [color=blue, penwidth=4, style=dotted];
    }`;
    const rect32 = new PdfRect(MARGIN, MM(140), MM(180), MM(80));
    page12.drawRectangle(new PdfPen(PdfColor.fromRgb(200, 200, 200), 0.5), null, rect32);
    page12.drawString(
      '32. Edge Color+Thickness+Style:',
      smallFont,
      PdfBrush.Black,
      null,
      new PdfRect(MARGIN, MM(135), MM(200), MM(5)),
      ETextAlign.CenterLeft
    );
    const content32 = dot.parse(edgeCombined, {});
    dot.draw(content32, rect32, ETextAlign.CenterLeft, { font, page: page12 });

    // Page 13: Alignment Tests
    const page13 = pdf.addPage();
    page13.drawString(
      'DOT Module Tests - Alignment',
      font,
      PdfBrush.Black,
      null,
      titleRect,
      ETextAlign.CenterLeft
    );

    // Test 33: TopLeft alignment
    const alignTest = `digraph G { a -> b -> c; }`;
    const rect33 = new PdfRect(MARGIN, MM(30), MM(90), MM(100));
    page13.drawRectangle(new PdfPen(PdfColor.fromRgb(200, 200, 200), 0.5), null, rect33);
    page13.drawString(
      '33. TopLeft:',
      smallFont,
      PdfBrush.Black,
      null,
      new PdfRect(MARGIN, MM(25), MM(200), MM(5)),
      ETextAlign.CenterLeft
    );
    const content33 = dot.parse(alignTest, {});
    dot.draw(content33, rect33, ETextAlign.TopLeft, { font, page: page13 });

    // Test 34: TopCenter alignment
    const rect34 = new PdfRect(MM(110), MM(30), MM(87), MM(100));
    page13.drawRectangle(new PdfPen(PdfColor.fromRgb(200, 200, 200), 0.5), null, rect34);
    page13.drawString(
      '34. TopCenter:',
      smallFont,
      PdfBrush.Black,
      null,
      new PdfRect(MM(110), MM(25), MM(200), MM(5)),
      ETextAlign.CenterLeft
    );
    const content34 = dot.parse(alignTest, {});
    dot.draw(content34, rect34, ETextAlign.TopCenter, { font, page: page13 });

    // Test 35: TopRight alignment
    const rect35 = new PdfRect(MM(110), MM(140), MM(87), MM(80));
    page13.drawRectangle(new PdfPen(PdfColor.fromRgb(200, 200, 200), 0.5), null, rect35);
    page13.drawString(
      '35. TopRight:',
      smallFont,
      PdfBrush.Black,
      null,
      new PdfRect(MM(110), MM(135), MM(200), MM(5)),
      ETextAlign.CenterLeft
    );
    const content35 = dot.parse(alignTest, {});
    dot.draw(content35, rect35, ETextAlign.TopRight, { font, page: page13 });

    // Test 36: CenterLeft alignment
    const rect36 = new PdfRect(MARGIN, MM(140), MM(90), MM(80));
    page13.drawRectangle(new PdfPen(PdfColor.fromRgb(200, 200, 200), 0.5), null, rect36);
    page13.drawString(
      '36. CenterLeft:',
      smallFont,
      PdfBrush.Black,
      null,
      new PdfRect(MARGIN, MM(135), MM(200), MM(5)),
      ETextAlign.CenterLeft
    );
    const content36 = dot.parse(alignTest, {});
    dot.draw(content36, rect36, ETextAlign.CenterLeft, { font, page: page13 });

    // Test 37: Center alignment
    const rect37 = new PdfRect(MARGIN, MM(230), MM(90), MM(60));
    page13.drawRectangle(new PdfPen(PdfColor.fromRgb(200, 200, 200), 0.5), null, rect37);
    page13.drawString(
      '37. Center:',
      smallFont,
      PdfBrush.Black,
      null,
      new PdfRect(MARGIN, MM(225), MM(200), MM(5)),
      ETextAlign.CenterLeft
    );
    const content37 = dot.parse(alignTest, {});
    dot.draw(content37, rect37, ETextAlign.Center, { font, page: page13 });

    // Test 38: BottomLeft alignment
    const rect38 = new PdfRect(MM(110), MM(230), MM(87), MM(60));
    page13.drawRectangle(new PdfPen(PdfColor.fromRgb(200, 200, 200), 0.5), null, rect38);
    page13.drawString(
      '38. BottomLeft:',
      smallFont,
      PdfBrush.Black,
      null,
      new PdfRect(MM(110), MM(225), MM(200), MM(5)),
      ETextAlign.CenterLeft
    );
    const content38 = dot.parse(alignTest, {});
    dot.draw(content38, rect38, ETextAlign.BottomLeft, { font, page: page13 });

    // Page 14: LaTeX Rendering Tests
    const page14 = pdf.addPage();
    page14.drawString(
      'DOT Module Tests - LaTeX Rendering',
      font,
      PdfBrush.Black,
      null,
      titleRect,
      ETextAlign.CenterLeft
    );

    // Test 39: LaTeX in nodes (verify centering)
    const latexNodes = `digraph G {
      a [label=<latex>\\alpha</latex>];
      b [label=<latex>\\frac{1}{2}</latex>];
      c [label=<latex>x^2 + y^2</latex>];
      d [label=<latex>\\sqrt{x}</latex>];
      a -> b -> c -> d;
    }`;
    const rect39 = new PdfRect(MARGIN, MM(30), MM(180), MM(100));
    page14.drawRectangle(new PdfPen(PdfColor.fromRgb(200, 200, 200), 0.5), null, rect39);
    page14.drawString(
      '39. LaTeX Node Labels:',
      smallFont,
      PdfBrush.Black,
      null,
      new PdfRect(MARGIN, MM(25), MM(200), MM(5)),
      ETextAlign.CenterLeft
    );
    const content39 = dot.parse(latexNodes, {});
    dot.draw(content39, rect39, ETextAlign.CenterLeft, { font, page: page14 });

    // Test 40: Mixed LaTeX and text
    const latexMixed = `digraph G {
      a [label="Start"];
      b [label=<latex>\\pi = 3.14</latex>];
      c [label=<latex>e^{i\\pi} + 1 = 0</latex>];
      d [label="End"];
      a -> b -> c -> d;
    }`;
    const rect40 = new PdfRect(MARGIN, MM(140), MM(180), MM(80));
    page14.drawRectangle(new PdfPen(PdfColor.fromRgb(200, 200, 200), 0.5), null, rect40);
    page14.drawString(
      '40. Mixed LaTeX + Text:',
      smallFont,
      PdfBrush.Black,
      null,
      new PdfRect(MARGIN, MM(135), MM(200), MM(5)),
      ETextAlign.CenterLeft
    );
    const content40 = dot.parse(latexMixed, {});
    dot.draw(content40, rect40, ETextAlign.CenterLeft, { font, page: page14 });

    // Page 15: Node Size Adjustment Tests
    const page15 = pdf.addPage();
    page15.drawString(
      'DOT Module Tests - Node Sizing',
      font,
      PdfBrush.Black,
      null,
      titleRect,
      ETextAlign.CenterLeft
    );

    // Test 41: Short labels (should have small nodes)
    const shortLabels = `digraph G {
      a -> b -> c;
    }`;
    const rect41 = new PdfRect(MARGIN, MM(30), MM(90), MM(80));
    page15.drawRectangle(new PdfPen(PdfColor.fromRgb(200, 200, 200), 0.5), null, rect41);
    page15.drawString(
      '41. Short Labels:',
      smallFont,
      PdfBrush.Black,
      null,
      new PdfRect(MARGIN, MM(25), MM(200), MM(5)),
      ETextAlign.CenterLeft
    );
    const content41 = dot.parse(shortLabels, {});
    dot.draw(content41, rect41, ETextAlign.CenterLeft, { font, page: page15 });

    // Test 42: Long labels (should expand nodes)
    const longLabels = `digraph G {
      a [label="Very Long Label"];
      b [label="Another Long Node Label Here"];
      c [label="Short"];
      a -> b -> c;
    }`;
    const rect42 = new PdfRect(MM(110), MM(30), MM(87), MM(80));
    page15.drawRectangle(new PdfPen(PdfColor.fromRgb(200, 200, 200), 0.5), null, rect42);
    page15.drawString(
      '42. Long Labels:',
      smallFont,
      PdfBrush.Black,
      null,
      new PdfRect(MM(110), MM(25), MM(200), MM(5)),
      ETextAlign.CenterLeft
    );
    const content42 = dot.parse(longLabels, {});
    dot.draw(content42, rect42, ETextAlign.CenterLeft, { font, page: page15 });

    // Test 43: Variable size labels
    const variableLabels = `digraph G {
      a [label="A"];
      b [label="Medium"];
      c [label="Very Long Label Text"];
      a -> b -> c;
    }`;
    const rect43 = new PdfRect(MARGIN, MM(120), MM(180), MM(80));
    page15.drawRectangle(new PdfPen(PdfColor.fromRgb(200, 200, 200), 0.5), null, rect43);
    page15.drawString(
      '43. Variable Size Labels:',
      smallFont,
      PdfBrush.Black,
      null,
      new PdfRect(MARGIN, MM(115), MM(200), MM(5)),
      ETextAlign.CenterLeft
    );
    const content43 = dot.parse(variableLabels, {});
    dot.draw(content43, rect43, ETextAlign.CenterLeft, { font, page: page15 });

    // Page 16: Clock Nodes in DOT Graphs
    const page16 = pdf.addPage();
    page16.drawString(
      'DOT Module Tests - Clock Nodes',
      font,
      PdfBrush.Black,
      null,
      titleRect,
      ETextAlign.CenterLeft
    );

    // Test 44: Clock nodes in DOT graph
    const clockNodes = `digraph G {
      node [shape=box];
      a [label=<clock>9:00</clock>];
      b [label=<clock>15:30</clock>];
      c [label=<clock>22:00</clock>];
      a -> b -> c;
    }`;
    const rect44 = new PdfRect(MARGIN, MM(30), MM(90), MM(80));
    page16.drawRectangle(new PdfPen(PdfColor.fromRgb(200, 200, 200), 0.5), null, rect44);
    page16.drawString(
      '44. Clock Nodes:',
      smallFont,
      PdfBrush.Black,
      null,
      new PdfRect(MARGIN, MM(25), MM(200), MM(5)),
      ETextAlign.CenterLeft
    );
    page16.drawString(
      '(9:00 -> 15:30 -> 22:00)',
      smallFont,
      PdfBrush.Black,
      null,
      new PdfRect(MARGIN, MM(115), MM(200), MM(5)),
      ETextAlign.CenterLeft
    );
    const content44 = dot.parse(clockNodes, {});
    dot.draw(content44, rect44, ETextAlign.CenterLeft, { font, page: page16 });

    // Test 45: Clock nodes with seconds
    const clockSeconds = `digraph G {
      node [shape=box];
      a [label=<clock>12:00:00</clock>];
      b [label=<clock>15:20:30</clock>];
      c [label=<clock>23:59:59</clock>];
      a -> b -> c;
    }`;
    const rect45 = new PdfRect(MM(110), MM(30), MM(87), MM(80));
    page16.drawRectangle(new PdfPen(PdfColor.fromRgb(200, 200, 200), 0.5), null, rect45);
    page16.drawString(
      '45. Clock Nodes with Seconds:',
      smallFont,
      PdfBrush.Black,
      null,
      new PdfRect(MM(110), MM(25), MM(200), MM(5)),
      ETextAlign.CenterLeft
    );
    page16.drawString(
      '(12:00:00 -> 15:20:30 -> 23:59:59)',
      smallFont,
      PdfBrush.Black,
      null,
      new PdfRect(MM(110), MM(115), MM(200), MM(5)),
      ETextAlign.CenterLeft
    );
    const content45 = dot.parse(clockSeconds, {});
    dot.draw(content45, rect45, ETextAlign.CenterLeft, { font, page: page16 });

    // Test 46: Mixed clock and text labels
    const mixedLabels = `digraph G {
      a [label="Morning"];
      b [label=<clock>9:00</clock>];
      c [label="Afternoon"];
      d [label=<clock>15:00</clock>];
      a -> b -> c -> d;
    }`;
    const rect46 = new PdfRect(MARGIN, MM(140), MM(180), MM(80));
    page16.drawRectangle(new PdfPen(PdfColor.fromRgb(200, 200, 200), 0.5), null, rect46);
    page16.drawString(
      '46. Mixed Clock + Text Labels:',
      smallFont,
      PdfBrush.Black,
      null,
      new PdfRect(MARGIN, MM(135), MM(200), MM(5)),
      ETextAlign.CenterLeft
    );
    const content46 = dot.parse(mixedLabels, {});
    dot.draw(content46, rect46, ETextAlign.CenterLeft, { font, page: page16 });

    // Test 47: Empty clock nodes
    const emptyClocks = `digraph G {
      node [shape=box];
      a [label=<clock></clock>];
      b [label=<clock>9:00</clock>];
      c [label=<clock></clock>];
      a -> b -> c;
    }`;
    const rect47 = new PdfRect(MARGIN, MM(230), MM(180), MM(60));
    page16.drawRectangle(new PdfPen(PdfColor.fromRgb(200, 200, 200), 0.5), null, rect47);
    page16.drawString(
      '47. Empty Clock Nodes:',
      smallFont,
      PdfBrush.Black,
      null,
      new PdfRect(MARGIN, MM(225), MM(200), MM(5)),
      ETextAlign.CenterLeft
    );
    const content47 = dot.parse(emptyClocks, {});
    dot.draw(content47, rect47, ETextAlign.CenterLeft, { font, page: page16 });
  }
}
